// Protected Route component for authentication with multi-tenant support

import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { hasRole } from '@/utils/roles';
import { useTenantSync } from '@/hooks/useTenantSync';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: string[];
  requireTenant?: boolean;
  requiredOrganization?: string;
  requireEmailVerification?: boolean;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireTenant = false,
  requiredOrganization,
  requireEmailVerification = false,
  redirectTo = '/login',
}) => {
  // Every authenticated shell (learner AppLayout and tenant AdminLayout) sits under
  // this route guard, so this is the one place to refresh plan / tenant status.
  useTenantSync();
  const location = useLocation();
  const {
    isAuthenticated,
    isAuthReady,
    isLoading,
    user
  } = useAuthStore();

  // For backward compatibility with multi-tenant props
  const tenant = user?.tenantId ? { id: user.tenantId, name: '' } : null;
  const currentOrganization = user?.organizationId ? { id: user.organizationId, name: user.organizationName || '' } : null;
  const organizations = currentOrganization ? [currentOrganization] : [];

  // Show loading state while auth is being validated or refreshed
  if (isLoading || !isAuthReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-tutara-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirect after login
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check registration status - redirect to appropriate page if registration is incomplete
  if (user?.registrationStatus && user.registrationStatus !== 'COMPLETE') {
    // Don't redirect if already on these pages
    const currentPath = location.pathname;
    if (currentPath === '/organization-setup' || currentPath === '/onboarding') {
      return <>{children}</>;
    }

    if (user.registrationStatus === 'NEEDS_ORG_SETUP') {
      return <Navigate to="/organization-setup" state={{ from: location }} replace />;
    }
    if (user.registrationStatus === 'NEEDS_ONBOARDING') {
      // Server status is authoritative: after a skip the backend returns COMPLETE
      // (forced shows exhausted) or NEEDS_ONBOARDING (first post-skip logins), so
      // NEEDS_ONBOARDING here always means the wizard should be shown.
      return <Navigate to="/onboarding" state={{ from: location }} replace />;
    }
    if (user.registrationStatus === 'NEEDS_PROFILE') {
      return <Navigate to="/profile/setup" state={{ from: location }} replace />;
    }
  }

  // Check email verification requirement
  if (requireEmailVerification && user) {
    // Note: Assuming email verification status is stored in user object
    // Adjust this based on your actual user object structure
    const userWithEmail = user as { emailVerified?: boolean; email_verified?: boolean };
    const isEmailVerified = userWithEmail.emailVerified || userWithEmail.email_verified;

    if (!isEmailVerified) {
      console.warn('[ProtectedRoute] Email verification required');
      return <Navigate to="/verify-email" state={{ from: location }} replace />;
    }
  }

  // Check tenant requirement
  if (requireTenant) {
    if (!user?.tenantId && !tenant) {
      console.warn('[ProtectedRoute] Tenant context required, redirecting to onboarding');
      return <Navigate to="/onboarding" state={{ from: location }} replace />;
    }
  }

  // Check specific organization requirement
  if (requiredOrganization) {
    const userBelongsToOrg = organizations.some(
      org => org.id === requiredOrganization
    );

    if (!userBelongsToOrg && currentOrganization?.id !== requiredOrganization) {
      console.warn('[ProtectedRoute] User does not belong to required organization');
      return <Navigate to="/dashboard" state={{ from: location }} replace />;
    }
  }

  // Check role-based access if required
  if (requiredRoles.length > 0 && user) {
    const hasRequiredRole = requiredRoles.some(role =>
      hasRole(user.roles, role)
    );

    if (!hasRequiredRole) {
      console.warn('[ProtectedRoute] User does not have required roles');
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User is authenticated and authorized
  return <>{children}</>;
};

export default ProtectedRoute;